import React, { useEffect, useState } from "react";

import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { NumberParam, StringParam, useQueryParams, withDefault } from "use-query-params";

import { Button, Divider, Tabs } from "antd";
import { ButtonType } from "antd/lib/button/button";

import { Page } from "../../components/page";
import { availableTripClass, TripClass } from "../../store/common_types";
import { removeSearchResults, startSearchRequest, unwrapLinkRequest } from "../../store/debug/actions";
import { RootState } from "../../store/root_reducer";
import { enumParams } from "../../utils/enum_params";
import { MomentParam } from "../../utils/moment_param";
import { useInterval } from "../../utils/use_interval";

import { ObjectViewer, SearchForm, SearchFormResult } from "./components";
import styles from "./styles.module.scss";

export const DebugPage: React.FC = () => {
  const { t } = useTranslation("debug_page");

  const debug = useSelector((rootState: RootState) => rootState.Debug);
  const selectedGate = useSelector((rootState: RootState) => rootState.User.selectedGate);

  const [clipboardState, setClipboardState] = useState({ value: "", ready: false, isLoading: false });

  const [searchState, setSearchState] = useQueryParams({
    origin: withDefault(StringParam, undefined),
    destination: withDefault(StringParam, undefined),
    departureDate: withDefault(MomentParam, undefined),
    returnDate: withDefault(MomentParam, undefined),

    adults: withDefault(NumberParam, 1),
    children: withDefault(NumberParam, 0),
    infants: withDefault(NumberParam, 0),

    tripClass: withDefault(enumParams<TripClass>(StringParam, availableTripClass), "Y"),
    locale: withDefault(StringParam, undefined),
    currency: withDefault(StringParam, undefined),
    market: withDefault(StringParam, undefined),
  });

  useInterval(() => {
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard
        .readText()
        .catch(() => "")
        .then((value) => {
          const hostRegexp = new RegExp(".*(aviasales|avs.io|wayaway.io|wway.io).*");
          setClipboardState((s) => ({ ...s, value, ready: hostRegexp.test(value) }));
        });
    }
  }, 1000);

  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(removeSearchResults());
  }, [dispatch, selectedGate]);

  const handleSearchFormFinish = (value: SearchFormResult) => {
    setSearchState(value);
    dispatch(startSearchRequest({ ...value, gateId: selectedGate.id }));
  };

  const handleLoadFromClipboard = () => {
    setClipboardState((s) => ({ ...s, isLoading: true }));
    dispatch(
      unwrapLinkRequest({
        url: clipboardState.value,
        onUnwrapSearch: (search) => {
          if (search) setSearchState(search, "push");
          setClipboardState((s) => ({ ...s, isLoading: false }));
        },
      })
    );
  };

  const buttonProps = { className: styles.button, download: true, type: "dashed" as ButtonType, target: "_blank" };

  return (
    <Page title={t("page.title")} description={t("page.description")}>
      <div className={styles.wrapper}>
        <div className={styles.controls}>
          <Tabs defaultActiveKey={"search"}>
            <Tabs.TabPane tab={t("page.tabs.forms.search")} key="search">
              <SearchForm
                key={JSON.stringify(searchState)}
                {...searchState}
                className={styles.form}
                errors={debug.errors.search}
                isLoading={debug.isLoading.startSearch || debug.isLoading.results}
                onFinish={handleSearchFormFinish}
              />
            </Tabs.TabPane>
          </Tabs>
          <Divider className={styles.divider} />
          {window.isSecureContext && (
            <Button
              className={styles.button}
              type="dashed"
              disabled={!clipboardState.ready}
              loading={clipboardState.isLoading}
              onClick={handleLoadFromClipboard}
            >
              {t("controls.loadFromClipboard")}
            </Button>
          )}
          <Button {...buttonProps} disabled={!debug.debugFile} href={debug.debugFile}>
            {t("controls.downloadDebug")}
          </Button>
          <Button {...buttonProps} disabled={!debug.resultsFile} href={debug.resultsFile}>
            {t("controls.downloadResults")}
          </Button>
          {debug.searchDebugFile && (
            <Button {...buttonProps} href={debug.searchDebugFile}>
              {t("controls.downloadSearchDebug")}
            </Button>
          )}
        </div>
        <Tabs defaultActiveKey={"results"}>
          <Tabs.TabPane
            tab={`${t("page.tabs.results")} ${(debug.results?.length || 0) > 0 ? `(${debug.results?.length})` : ""}`}
            key="results"
          >
            <ObjectViewer data={debug.results} />
          </Tabs.TabPane>
          {debug.searchDebug && (
            <Tabs.TabPane tab={t("page.tabs.debug")} key="debug">
              <ObjectViewer data={debug.searchDebug} />
            </Tabs.TabPane>
          )}
        </Tabs>
      </div>
    </Page>
  );
};
